import { useState, useEffect } from 'react';
import { Source, Layer, Marker, useMap } from 'react-map-gl/mapbox';
import type { FillLayerSpecification, LineLayerSpecification } from 'mapbox-gl';
import { useGcsStore } from '../../../store/useGcsStore';
import type { ControlZone } from '../../../types/gcs/flightNetwork';
import { getMapElementZIndex, MapElement } from '../../../types/ui/mapElement';
import { getServerNameCode } from '../../../config/gcs/gcsConfigs';
import { Text } from '../../../components/general/Text';

interface GcsControlZoneProps {
  gcsId: string;
}

// Zoom level threshold: above this level (zoomed out) show marker, below (zoomed in) show polygon
const CONTROL_ZONE_ZOOM_THRESHOLD = 14;

const CONTROL_ZONE_FILL_COLOR = '#B2B2B2';
const CONTROL_ZONE_STROKE_COLOR = '#ffffff';
const CONTROL_ZONE_FILL_OPACITY = 0.5;

const controlZoneFillLayer: FillLayerSpecification = {
  id: 'control-zone-fill',
  type: 'fill',
  source: 'control-zone',
  paint: {
    'fill-color': CONTROL_ZONE_FILL_COLOR,
    'fill-opacity': CONTROL_ZONE_FILL_OPACITY
  }
};

const controlZoneLineLayer: LineLayerSpecification = {
  id: 'control-zone-line',
  type: 'line',
  source: 'control-zone',
  paint: {
    'line-color': CONTROL_ZONE_STROKE_COLOR,
    'line-width': 3
  }
};

const controlZoneToGeoJSON = (controlZone: ControlZone) => {
  const coordinates = controlZone.points.map((p) => [p.lng, p.lat]);
  // Close the polygon by adding the first point at the end
  if (coordinates.length > 0) {
    coordinates.push(coordinates[0]);
  }
  return {
    type: 'Feature' as const,
    geometry: {
      type: 'Polygon' as const,
      coordinates: [coordinates]
    },
    properties: {}
  };
};

const calculateControlZoneCenter = (controlZone: ControlZone) => {
  const points = controlZone.points;
  if (points.length === 0) {
    return { lng: 0, lat: 0 };
  }

  const sum = points.reduce(
    (acc, point) => ({
      lng: acc.lng + point.lng,
      lat: acc.lat + point.lat
    }),
    { lng: 0, lat: 0 }
  );

  return {
    lng: sum.lng / points.length,
    lat: sum.lat / points.length
  };
};

export const GcsControlZone = ({ gcsId }: GcsControlZoneProps) => {
  const { current: map } = useMap();
  const [zoom, setZoom] = useState<number>(map?.getZoom() ?? 10);

  const controlZone = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    return gcsData?.flightNetwork.data?.centerControlZone;
  });

  useEffect(() => {
    if (!map) return;

    const handleZoom = () => {
      setZoom(map.getZoom());
    };

    // Set initial zoom
    handleZoom();

    // Listen to zoom changes
    map.on('zoomend', handleZoom);

    return () => {
      map.off('zoomend', handleZoom);
    };
  }, [map]);

  if (!controlZone) {
    return null;
  }

  const center = calculateControlZoneCenter(controlZone);
  const showMarker = zoom < CONTROL_ZONE_ZOOM_THRESHOLD;
  const nameCode = getServerNameCode(gcsId);

  if (showMarker) {
    return (
      <Marker
        longitude={center.lng}
        latitude={center.lat}
        anchor='center'
        style={{ zIndex: getMapElementZIndex(MapElement.CONTROL_ZONE) }}
      >
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '8px',
            backgroundColor: `rgba(178, 178, 178, ${CONTROL_ZONE_FILL_OPACITY})`,
            border: `3px solid ${CONTROL_ZONE_STROKE_COLOR}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Text variant='body1-bold' style={{ color: 'white' }}>
            {nameCode}
          </Text>
        </div>
      </Marker>
    );
  }

  return (
    <Source id={`control-zone-${gcsId}`} type='geojson' data={controlZoneToGeoJSON(controlZone)}>
      <Layer {...controlZoneFillLayer} id={`control-zone-fill-${gcsId}`} source={`control-zone-${gcsId}`} />
      <Layer {...controlZoneLineLayer} id={`control-zone-line-${gcsId}`} source={`control-zone-${gcsId}`} />
    </Source>
  );
};
